/**
 * WebGLのコントローラを作るファイル
 *
 * 一人称視点ではOrbitControls、三人称視点ではCameraControlsを使用
 *
 * CameraControlsについては以下を参照
 * https://github.com/yomotsu/camera-controls
 */

import * as THREE from "three";
import { Vector2, Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import CameraControls from "camera-controls";
import TWEEN from "@tweenjs/tween.js";

import store from "@/store";
import configRoom from "@/config/room";

const cameraInitMode = configRoom.cameraInitMode;
const EPS = 1e-5;
const durationVal = 400;
const minDurationVal = 700;
const firstPersonHeight = 1.4;

const firstPersonP = new Vector3(0, 0, 0);
const firstPersonNextP = new Vector3(0, 0, 0);

const V0 = new Vector2(0, 0);
const V1 = new Vector2(0, 0);

const baseP = new Vector3(0, 0, 0);
const targetP = new Vector3(0, 0, 0);
const nowP = new Vector3(0, 0, 0);

CameraControls.install({ THREE: THREE });

export default class Controls {
  constructor() {
    // コントローラの動作制御状態
    this.isRotate = true;
    this.isTranlrate = true;
    this.isScale = true;

    // アニメーション中のフラグ
    this.isAnimation = false;

    // カレントされているコントローラ
    this.activeControls = null;

    // 1人称用のコントローラ（CameraControls）
    this.firstPersonCtl = null;

    // 3人称用のコントローラ（OrbitControls）
    this.thirdPersonCtl = null;

    firstPersonP.setY(firstPersonHeight);
  }
  start(camera, domElement) {
    this.camera = camera;
    this.domElement = domElement;

    this.firstPersonCtl = new CameraControls(camera, domElement);
    this.firstPersonCtl.mouseButtons.left = CameraControls.ACTION.ROTATE;
    this.firstPersonCtl.mouseButtons.right = CameraControls.ACTION.NONE;
    this.firstPersonCtl.touches.two = CameraControls.ACTION.NONE;
    this.firstPersonCtl.dampingFactor = 1;
    this.firstPersonCtl.azimuthRotateSpeed = -0.3;
    this.firstPersonCtl.polarRotateSpeed = -0.3;
    this.firstPersonCtl.truckSpeed = (1 / EPS) * 3;
    this.firstPersonCtl.enablePan = false;
    this.firstPersonCtl.saveState();

    this.thirdPersonCtl = new OrbitControls(camera, domElement);
    this.thirdPersonCtl.zoomSpeed = 0.3;
    this.thirdPersonCtl.minPolarAngle = -(Math.PI / 180) * 90;
    this.thirdPersonCtl.maxPolarAngle = (Math.PI / 180) * 90;
    this.thirdPersonCtl.enableRotate = false;
    this.thirdPersonCtl.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_PAN
    };

    this.firstPersonCtl.enabled = true;
    this.thirdPersonCtl.enabled = false;

    this.moveInitFirstPersonPosition();

    // Initialize
    this.changeMode(cameraInitMode);
  }
  dispose() {
    if (this.activeControls !== null) {
      this.activeControls.dispose();
      this.activeControls = null;
    }
  }
  update(delta) {
    TWEEN.update();

    if (this.activeControls === null) return;
    this.activeControls.update(delta);
  }
  changeMode(mode) {
    /**
     * OrbitControls: 三人称視点 俯瞰モード時のコントローラ
     * CameraControls: 一人称視点 ウォークスルーモード時のコントローラ
     */
    if (mode === "firstPerson") {
      this.activeControls = this.firstPersonCtl;
      this.firstPersonCtl.enabled = true;
      this.thirdPersonCtl.enabled = false;
    } else if (mode === "thirdPerson") {
      this.activeControls = this.thirdPersonCtl;
      this.firstPersonCtl.enabled = false;
      this.thirdPersonCtl.enabled = true;
      this.activeControls.target.set(0, 0, 0);
    }
  }

  /**
   * 一人称初期位置を設定する
   */
  setInitFirstPersonPosition(data) {
    const startId = data.start;
    let startIndex = 0;
    for (let i = 0; i < data.node.length; i++) {
      if (data.node[i].node_id === startId) {
        startIndex = i;
      }
    }
    // startIndexのノードのnext_idがnullの場合、startIndexを0にする
    if (data.node[startIndex].next_id === null) {
      startIndex = 0;
    }

    const { meterPerPixel } = store.state.webgl;
    firstPersonP.setX(data.node[startIndex].x * meterPerPixel);
    firstPersonP.setZ(data.node[startIndex].y * meterPerPixel);

    let nextIndex = 0;
    for (let i = 0; i < data.node.length; i++) {
      if (data.node[i].node_id === data.node[startIndex].next_id) {
        nextIndex = i;
      }
    }

    // startIndexとnextIndexが同じだった場合、向き先（nextIndex）を1つ次にする
    if (startIndex === nextIndex) {
      nextIndex++;
    }

    const nx = data.node[nextIndex].x * meterPerPixel;
    const ny = data.node[nextIndex].y * meterPerPixel;
    firstPersonNextP.set(-nx, firstPersonP.y, ny);
  }

  setNode(data) {
    this.dataNode = data;
  }

  /**
   * 一人称初期位置に移動する
   */
  moveInitFirstPersonPosition() {
    // 一人称の初期位置
    this.firstPersonCtl.moveTo(
      firstPersonP.x,
      firstPersonP.y,
      firstPersonP.z,
      false
    );
    baseP.set(firstPersonP.x, firstPersonP.y, firstPersonP.z);

    V0.set(0, -1);
    V1.set(firstPersonNextP.x - baseP.x, firstPersonNextP.z - baseP.z);

    const denominator = Math.sqrt(V0.lengthSq() * V1.lengthSq());
    const theta = V0.dot(V1) / denominator;
    const angle = V1.x < 0 ? -Math.acos(theta) : Math.acos(theta);
    let initAzAngle = -this.firstPersonCtl.azimuthAngle - angle;

    this.firstPersonCtl.rotate(
      initAzAngle,
      -this.firstPersonCtl.polarAngle + Math.PI / 2,
      false
    );
  }

  /**
   * 一人称の視点移動
   *
   * @param {[{x: Number, y: Number}]} positions
   * @param {boolean} isRotation - 回転アニメーションをするか
   */
  movePosition(positions, isRotation, duration = null) {
    if (this.activeControls === null) return;
    if (store.state.webgl.camera.mode === "thirdPerson") return;

    this.isAnimation = true;
    this.movePositionAnimation(0, positions, isRotation, duration);
  }

  movePositionAnimation(index, positions, isRotation, duration) {
    if (index === positions.length) {
      this.isAnimation = false;
      return;
    }

    targetP.set(positions[index].x, firstPersonP.y, positions[index].y);

    const distance = targetP.distanceTo(baseP);
    let tDuration = Math.max(
      Math.floor(distance) * durationVal,
      minDurationVal
    );
    if (duration !== null) tDuration = duration;

    if (isRotation) {
      this.moveRotationAnimation(tDuration);
    }

    new TWEEN.Tween({ progress: 0 })
      .to(
        {
          progress: 1
        },
        tDuration
      )
      .easing(TWEEN.Easing.Linear.None)
      .onStart(() => {
        this.activeControls.enabled = false;
      })
      .onUpdate(vals => {
        nowP.set(
          (targetP.x - baseP.x) * vals.progress,
          (targetP.y - baseP.y) * vals.progress,
          (targetP.z - baseP.z) * vals.progress
        );
        nowP.add(baseP);

        this.activeControls.moveTo(nowP.x, nowP.y, nowP.z, false);
      })
      .onComplete(() => {
        this.activeControls.enabled = true;
        baseP.set(targetP.x, targetP.y, targetP.z);

        this.movePositionAnimation(index + 1, positions, isRotation, duration);
      })
      .start();
  }

  moveRotationAnimation(duration) {
    const tDuration = duration * 0.2;
    const updateNum = (tDuration / 1000) * 60 + 1;

    V0.set(0, -1);
    V1.set(targetP.x - baseP.x, targetP.z - baseP.z);

    const denominator = Math.sqrt(V0.lengthSq() * V1.lengthSq());
    const theta = V0.dot(V1) / denominator;
    const angle = V1.x < 0 ? -Math.acos(theta) : Math.acos(theta);

    let initAzAngle = -this.activeControls.azimuthAngle - angle;
    if (Math.PI < initAzAngle) {
      initAzAngle = -Math.PI * 2 + initAzAngle;
    } else if (initAzAngle < -Math.PI) {
      initAzAngle = Math.PI * 2 + initAzAngle;
    }
    const initPoAngle = -this.activeControls.polarAngle + Math.PI / 2;

    new TWEEN.Tween({ progress: 0 })
      .to(
        {
          progress: 1
        },
        tDuration
      )
      .easing(TWEEN.Easing.Linear.None)
      .onStart(() => {})
      .onUpdate(() => {
        this.activeControls.rotate(
          initAzAngle / updateNum,
          initPoAngle / updateNum,
          false
        );
      })
      .onComplete(() => {})
      .start();
  }

  rotateToNode(id) {
    let currentNode = null;
    let currentIndex = -1;
    for (let i = 0; i < this.dataNode.length; i++) {
      if (this.dataNode[i].node_id === id) {
        currentNode = this.dataNode[i];
        currentIndex = i;
      }
    }

    const nextId = this.dataNode[currentIndex].next_id;

    let nextNode = null;
    for (let i = 0; i < this.dataNode.length; i++) {
      if (this.dataNode[i].node_id === nextId) {
        nextNode = this.dataNode[i];
      }
    }

    V0.set(0, -1);
    V1.set(nextNode.x - currentNode.x, nextNode.y - currentNode.y);

    const denominator = Math.sqrt(V0.lengthSq() * V1.lengthSq());
    const theta = V0.dot(V1) / denominator;
    const angle = V1.x < 0 ? -Math.acos(theta) : Math.acos(theta);
    let initAzAngle = -this.firstPersonCtl.azimuthAngle - angle;

    this.firstPersonCtl.rotate(
      initAzAngle,
      -this.firstPersonCtl.polarAngle + Math.PI / 2,
      false
    );
  }

  rotateAnimationByAngle(angle) {
    const tDuration = 300;
    const updateNum = (tDuration / 1000) * 60 + 1;

    const initAzAngle = (Math.PI / 180) * angle;
    const initPoAngle = -this.activeControls.polarAngle + Math.PI / 2;

    new TWEEN.Tween({ progress: 0 })
      .to(
        {
          progress: 1
        },
        tDuration
      )
      .easing(TWEEN.Easing.Linear.None)
      .onStart(() => {})
      .onUpdate(() => {
        this.activeControls.rotate(
          initAzAngle / updateNum,
          initPoAngle / updateNum,
          false
        );
      })
      .onComplete(() => {})
      .start();
  }
}
